import { useCallback, useMemo, useState } from "react";

/** Generic multi-select for entity list bulk export. Independent of detail selection. */
export function useEntityBulkSelection<T extends { id: string }>(
  items: readonly T[],
  filtered: readonly T[],
) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const item of filtered) next.add(item.id);
      return next;
    });
  }, [filtered]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((item) => selectedIds.has(item.id));

  const selectedItems = useMemo(() => {
    if (selectedIds.size === 0) return [] as T[];
    const byId = new Map(items.map((item) => [item.id, item] as const));
    const out: T[] = [];
    for (const id of selectedIds) {
      const item = byId.get(id);
      if (item) out.push(item);
    }
    return out;
  }, [items, selectedIds]);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    selectedItems,
    toggleSelected,
    selectAllFiltered,
    clearSelection,
    allFilteredSelected,
  };
}
